<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ $t("finance.reports2.common.expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ $t("finance.reports2.common.collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ $t("finance.reports2.common.exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!filters.date" @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ $t("finance.reports2.common.exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ $t("finance.reports2.common.print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.reports2.common.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field
            v-model="filters.date"
            type="date"
            :label="t('reportDate')"
            hide-details
            class="fin-field"
            @change="onDateChange"
          ></v-text-field>
          <v-text-field
            v-model="filters.year_start"
            type="date"
            :label="t('yearStart')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-select
            v-model="filters.max_level"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="t('maxLevel')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.type"
            :items="typeOptions"
            item-text="text"
            item-value="value"
            :label="t('accountType')"
            hide-details
            class="fin-field"
          ></v-select>
          <account-picker v-model="filters.parent_id" :label="t('parentAccount')" field-class="fin-field ab-parent" />
          <v-select
            v-if="currencyOptions.length > 1"
            v-model="filters.currency"
            :items="currencyOptions"
            :label="$t('finance.reports2.common.currency')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-text-field
            v-model="filters.balance_from"
            type="number"
            :label="t('balanceFrom')"
            hide-details
            clearable
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.balance_to"
            type="number"
            :label="t('balanceTo')"
            hide-details
            clearable
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.search"
            :label="t('search')"
            hide-details
            clearable
            class="fin-field"
            @keyup.enter="loadReport"
          ></v-text-field>
          <v-checkbox v-model="filters.hide_zero" :label="t('hideZero')" hide-details class="ab-check"></v-checkbox>
          <v-checkbox
            v-model="filters.show_children"
            :label="t('showChildren')"
            hide-details
            class="ab-check"
          ></v-checkbox>
          <v-btn small color="primary" @click="loadReport">{{ $t("finance.reports2.common.apply") }}</v-btn>
        </div>
      </div>

      <div v-if="hasReport && report.alerts.length" class="ab-alerts fr-no-print">
        <div
          v-for="(alert, index) in report.alerts"
          :key="'alert-' + index"
          :class="alert.level === 'error' ? 'fin-alert-error ab-alert-error' : 'ab-alert-warn'"
        >
          <v-icon small :color="alert.level === 'error' ? '#c62828' : '#ef6c00'">warning</v-icon>
          <b>{{ alert.text }}</b>
          <span v-if="alert.detail"> - {{ alert.detail }}</span>
        </div>
      </div>

      <div v-if="hasReport" class="fin-summary fr-no-print">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("accountsCount") }}</span>
          <span class="fin-stat-value">{{ report.summary.accounts_count }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("debitTotal") }}</span>
          <span class="fin-num fin-stat-value">{{ formatMoney(report.summary.debit_total) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("creditTotal") }}</span>
          <span class="fin-num fin-stat-value">{{ formatMoney(report.summary.credit_total) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("difference") }}</span>
          <span class="fin-num fin-stat-value">{{ formatMoney(report.summary.difference) }}</span>
        </div>
        <v-chip small :color="report.ledger.is_balanced ? 'green' : 'red'" text-color="white">
          <v-icon small class="me-1">{{ report.ledger.is_balanced ? "check_circle" : "warning" }}</v-icon>
          {{ t("ledgerStatus") }}: {{ ledgerStatusLabel }}
        </v-chip>
        <v-chip v-if="report.summary.abnormal_count" small color="orange" text-color="white">
          <v-icon small class="me-1">warning</v-icon>
          {{ t("abnormalCount") }}: {{ report.summary.abnormal_count }}
        </v-chip>
        <span v-if="!report.summary.is_full_ledger" class="ab-scope-hint">{{ t("scopedSummary") }}</span>
      </div>

      <div class="ab-table-wrap fin-table-wrap">
        <table class="ab-tree-table fin-table">
          <thead>
            <tr>
              <th class="ab-col-serial">{{ t("columns.serial") }}</th>
              <th>{{ t("columns.code") }}</th>
              <th class="ab-col-name">{{ t("columns.name") }}</th>
              <th>{{ t("columns.level") }}</th>
              <th>{{ t("columns.parent") }}</th>
              <th>{{ t("columns.normalBalance") }}</th>
              <th class="fin-num-head">{{ t("columns.balance") }}</th>
              <th>{{ t("columns.balanceSide") }}</th>
              <th class="ab-col-notes">{{ t("columns.notes") }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in visibleRows">
              <tr v-if="item.group" :key="'group-' + item.key" class="ab-group-row">
                <td colspan="9">{{ t("unclassified") }}</td>
              </tr>
              <tr
                v-else
                :key="item.account.id"
                :class="rowClasses(item)"
                :title="item.account.matches_filters ? '' : t('contextRow')"
              >
                <td class="ab-col-serial">{{ item.account.serial }}</td>
                <td class="fin-code">{{ item.account.code }}</td>
                <td class="ab-col-name">
                  <span class="ab-indent" :style="{ 'padding-inline-start': item.depth * 22 + 'px' }">
                    <v-btn
                      v-if="item.hasChildren"
                      icon
                      small
                      class="ab-toggle fr-no-print"
                      @click="toggleNode(item.account.id)"
                    >
                      <v-icon small>{{ isExpanded(item.account.id) ? "remove" : "add" }}</v-icon>
                    </v-btn>
                    <span v-else class="ab-toggle-spacer"></span>
                    <span class="fin-ellipsis" :title="accountName(item.account)">{{ accountName(item.account) }}</span>
                  </span>
                </td>
                <td>{{ item.account.level }}</td>
                <td>{{ parentText(item.account) }}</td>
                <td>{{ item.account.normal_balance_label }}</td>
                <td class="fin-num" :class="{ 'ab-negative': item.account.balance < 0 }">
                  {{ formatMoney(item.account.balance) }}
                </td>
                <td>
                  <span class="ab-side" :class="'ab-side-' + item.account.balance_side">
                    {{ item.account.balance_side_label }}
                  </span>
                </td>
                <td class="ab-col-notes">
                  <span
                    v-for="(alert, index) in item.account.alerts"
                    :key="item.account.id + '-' + index"
                    class="ab-note"
                    :class="'ab-note-' + alert.code"
                  >
                    {{ alert.text }}
                  </span>
                </td>
              </tr>
            </template>
            <tr v-if="!visibleRows.length && !loading">
              <td colspan="9" class="fin-empty">{{ $t("finance.reports2.common.noData") }}</td>
            </tr>
          </tbody>
          <tfoot v-if="hasReport">
            <tr class="ab-totals-row">
              <td colspan="6">
                {{ t("debitTotal") }}: {{ formatMoney(report.summary.debit_total) }} &nbsp;|&nbsp; {{ t("creditTotal") }}:
                {{ formatMoney(report.summary.credit_total) }}
              </td>
              <td class="fin-num">{{ formatMoney(report.summary.difference) }}</td>
              <td colspan="2" :class="report.summary.is_balanced ? 'ab-match-ok' : 'ab-match-bad'">
                {{ report.summary.status_label }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-if="hasReport && report.notes.length" class="ab-notes">
        <div class="ab-notes-title">{{ t("notesTitle") }}</div>
        <p v-for="(note, index) in report.notes" :key="'note-' + index">{{ note }}</p>
      </div>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.6 of the accountant's document 2026-10-03: ميزان المحاسبة
 * (الرصيد التراكمي). Every account's cumulative balance up to the report date
 * (opening of the year + the year's movement), in chart-tree order with
 * roll-ups, WITHOUT debit/credit movement columns. Modelled on TrialBalance.vue.
 *
 * API: this.api.ACTIONS.ACCOUNTING_ACCOUNTING_BALANCE (GET, format=json|csv).
 * Money in the payload is STORED (old-unit) SYP; the balance range inputs are
 * typed in display units and converted with toStoredAmount before sending.
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import AccountPicker from "@/components/finance/AccountPicker";
import financeReportExport from "@/mixins/financeReportExport";
import { companyToday } from "@/util/companyTime";

const TYPES = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"];

// Final-version doc 2026-10-05: the company (Damascus) day on the
// server-synced clock, not the PC calendar.
function todayString() {
  return companyToday();
}

function yearStartOf(date) {
  return String(date || todayString()).slice(0, 4) + "-01-01";
}

export default {
  name: "AccountingBalance",
  title() {
    return this.$t("finance.reports2.accountingBalance.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent, AccountPicker },
  mixins: [financeReportExport],
  data() {
    const today = todayString();
    return {
      loading: false,
      report: null,
      expandedIds: {},
      filters: {
        date: today,
        year_start: yearStartOf(today),
        currency: "SYP",
        max_level: null,
        type: null,
        parent_id: null,
        balance_from: null,
        balance_to: null,
        search: null,
        hide_zero: true,
        show_children: true,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.summary);
    },
    reportRows() {
      return (this.report && this.report.rows) || [];
    },
    rowsById() {
      const map = {};
      this.reportRows.forEach((row) => {
        map[row.id] = row;
      });
      return map;
    },
    childrenByParent() {
      // Rows arrive in tree order (orphan subtrees last); a parent missing
      // from the filtered payload turns its children into roots.
      const map = {};
      this.reportRows.forEach((row) => {
        const parentId = row.parent_id && this.rowsById[row.parent_id] ? row.parent_id : 0;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(row);
      });
      return map;
    },
    visibleRows() {
      return this.treeRows(true);
    },
    levelOptions() {
      const options = [{ text: this.t("allLevels"), value: null }];
      for (let level = 1; level <= 8; level++) {
        options.push({ text: String(level), value: level });
      }
      return options;
    },
    typeOptions() {
      return [{ text: this.t("allTypes"), value: null }].concat(
        TYPES.map((type) => ({ text: this.t("types." + type), value: type }))
      );
    },
    currencyOptions() {
      const list = (this.report && this.report.currencies) || ["SYP"];
      return list.indexOf(this.filters.currency) === -1 ? list.concat([this.filters.currency]) : list;
    },
    ledgerStatusLabel() {
      return this.report && this.report.ledger.is_balanced
        ? this.$t("finance.reports2.common.balanced")
        : this.$t("finance.reports2.common.notBalanced");
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.accountingBalance." + key);
    },
    onDateChange(value) {
      // Keep the default fiscal year (1 January of the report date's year)
      // unless the user typed another start.
      if (value && (!this.filters.year_start || this.filters.year_start.slice(5) === "01-01")) {
        this.filters.year_start = yearStartOf(value);
      }
    },
    buildParams() {
      const f = this.filters;
      const params = {
        date: f.date,
        hide_zero: f.hide_zero ? 1 : 0,
        show_children: f.show_children ? 1 : 0,
      };
      if (f.year_start) params.year_start = f.year_start;
      if (f.currency) params.currency = f.currency;
      if (f.max_level) params.max_level = f.max_level;
      if (f.type) params.type = f.type;
      if (f.parent_id) params.parent_id = f.parent_id;
      const from = this.toStoredAmount(f.balance_from);
      const to = this.toStoredAmount(f.balance_to);
      if (from !== null) params.balance_from = from;
      if (to !== null) params.balance_to = to;
      if (f.search && String(f.search).trim()) params.search = String(f.search).trim();
      return params;
    },
    loadReport() {
      if (!this.filters.date) {
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ACCOUNTING_BALANCE, { params: this.buildParams() })
        .then((response) => {
          this.report = response.data.data || null;
          // Accountants read the whole structure by default.
          this.expandAll();
        })
        .catch((error) => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.frErrorMessage(error, this.$t("finance.reports2.common.failedLoad")),
            "red"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * Tree walk in display order. onlyExpanded=false ignores the expand
     * state (exports carry the whole filtered report). A group marker row
     * precedes the first unclassified (orphan) root.
     */
    treeRows(onlyExpanded) {
      const rows = [];
      let groupAdded = false;
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          if (depth === 0 && account.is_unclassified && !groupAdded) {
            rows.push({ group: true, key: account.id });
            groupAdded = true;
          }
          const hasChildren = !!(this.childrenByParent[account.id] || []).length;
          rows.push({ account, depth, hasChildren });
          if (hasChildren && (!onlyExpanded || this.expandedIds[account.id])) {
            const nextSeen = Object.assign({}, seen);
            nextSeen[account.id] = true;
            walk(account.id, depth + 1, nextSeen);
          }
        });
      };
      walk(0, 0, {});
      return rows;
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    toggleNode(id) {
      const next = Object.assign({}, this.expandedIds);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.expandedIds = next;
    },
    expandAll() {
      const next = {};
      this.reportRows.forEach((row) => {
        next[row.id] = true;
      });
      this.expandedIds = next;
    },
    collapseAll() {
      this.expandedIds = {};
    },
    rowClasses(item) {
      const account = item.account;
      return {
        "ab-level-1": account.level === 1,
        "ab-level-2": account.level === 2,
        "ab-parent-row": item.hasChildren,
        "ab-abnormal-row": account.is_abnormal,
        "ab-context-row": !account.matches_filters,
        "ab-inactive-row": !account.is_active,
      };
    },
    accountName(account) {
      if (!account) {
        return "-";
      }
      if (this.frIsArabic() && account.name_ar) {
        return account.name_ar;
      }
      return account.name || account.name_ar || "-";
    },
    parentText(account) {
      if (account.parent_code) {
        return account.parent_code + " - " + (account.parent_name || "");
      }
      return account.is_orphan ? this.t("unclassified") : "";
    },
    // ---------------------------------------------------------------
    // Exports
    // ---------------------------------------------------------------
    exportColumns() {
      return [
        { key: "serial", label: this.t("columns.serial"), type: "count", width: 6 },
        { key: "code", label: this.t("columns.code"), type: "code", width: 18 },
        { key: "name", label: this.t("columns.name"), type: "text", width: 40, indent: true },
        { key: "level", label: this.t("columns.level"), type: "number", width: 8 },
        { key: "parent", label: this.t("columns.parent"), type: "text", width: 30 },
        { key: "normal", label: this.t("columns.normalBalance"), type: "text", width: 10 },
        { key: "balance", label: this.t("columns.balance"), type: "money", width: 18 },
        { key: "side", label: this.t("columns.balanceSide"), type: "text", width: 10 },
        { key: "notes", label: this.t("columns.notes"), type: "text", width: 36 },
      ];
    },
    exportRows() {
      const rows = [];
      this.treeRows(false).forEach((item) => {
        if (item.group) {
          rows.push({ name: this.t("unclassified"), _kind: "section" });
          return;
        }
        const account = item.account;
        rows.push({
          serial: account.serial,
          code: String(account.code),
          name: this.accountName(account),
          level: account.level,
          parent: this.parentText(account),
          normal: account.normal_balance_label,
          balance: account.balance,
          side: account.balance_side_label,
          notes: account.notes,
          _level: item.depth,
          _bold: account.level <= 2,
          _highlight: account.is_abnormal ? "warn" : undefined,
        });
      });
      return rows;
    },
    summaryRows() {
      const s = this.report.summary;
      return [
        { name: this.t("summaryTitle"), _kind: "section" },
        { name: this.t("accountsCount"), notes: String(s.accounts_count), _kind: "subtotal" },
        { name: this.t("debitTotal"), balance: s.debit_total, _kind: "subtotal" },
        { name: this.t("creditTotal"), balance: s.credit_total, _kind: "subtotal" },
        { name: this.t("difference"), balance: s.difference, _kind: "subtotal" },
        {
          name: this.t("status"),
          side: this.report.ledger.status_label,
          _kind: "total",
          _highlight: this.report.ledger.is_balanced ? "ok" : "error",
        },
        { name: this.t("abnormalCount"), notes: String(s.abnormal_count), _kind: "subtotal" },
      ];
    },
    exportNotes() {
      const notes = [];
      (this.report.alerts || []).forEach((alert) => {
        notes.push(alert.text + (alert.detail ? " - " + alert.detail : ""));
      });
      return notes.concat(this.report.notes || []);
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      // Her export rule: Excel, one sheet.
      this.frExportXlsx({
        fileName: "accounting-balance",
        header: this.report.header,
        sheets: [
          {
            name: this.t("sheetName"),
            columns: this.exportColumns(),
            rows: this.exportRows().concat([{}], this.summaryRows()),
            notes: this.exportNotes(),
          },
        ],
      });
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      const s = this.report.summary;
      const summaryColumns = [
        { key: "label", label: this.t("summaryTitle"), type: "text" },
        { key: "value", label: "", type: "text" },
      ];
      const summaryRows = [
        { label: this.t("accountsCount"), value: String(s.accounts_count) },
        { label: this.t("debitTotal"), value: this.formatMoney(s.debit_total) },
        { label: this.t("creditTotal"), value: this.formatMoney(s.credit_total) },
        { label: this.t("difference"), value: this.formatMoney(s.difference) },
        {
          label: this.t("status"),
          value: this.report.ledger.status_label,
          _highlight: this.report.ledger.is_balanced ? "ok" : "error",
        },
        { label: this.t("abnormalCount"), value: String(s.abnormal_count) },
      ];
      // Her export rule: A4 portrait.
      this.frPrint({
        header: this.report.header,
        orientation: "portrait",
        sections: [
          { columns: this.exportColumns(), rows: this.exportRows() },
          { columns: summaryColumns, rows: summaryRows },
        ],
        notes: this.exportNotes(),
      });
    },
    downloadCsv() {
      if (!this.filters.date) {
        return;
      }
      this.loading = true;
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_ACCOUNTING_BALANCE, this.buildParams(), "accounting-balance").finally(
        () => {
          this.loading = false;
        }
      );
    },
  },
};
</script>

<style scoped>
.ab-check {
  flex: 0 0 auto;
  margin: 0 6px;
}
.ab-alerts {
  padding: 6px 12px 0;
}
.ab-alert-warn {
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 6px;
  color: #6d4c41;
}
.ab-alert-error {
  margin-bottom: 6px;
}
.ab-scope-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.ab-table-wrap {
  --fin-table-max-h: calc(100vh - 300px);
  min-height: 320px;
}
.ab-col-serial {
  width: 44px;
  color: rgba(0, 0, 0, 0.55);
}
.ab-col-notes {
  white-space: normal;
  min-width: 200px;
}
.ab-parent-row td {
  background: #f5f7fa;
}
.ab-level-1 td {
  font-weight: 700;
  font-size: 14px;
}
.ab-level-2 td {
  font-weight: 700;
}
.ab-abnormal-row td {
  background: #fff3e0;
}
.ab-context-row td {
  color: rgba(0, 0, 0, 0.45);
}
.ab-inactive-row td {
  color: rgba(0, 0, 0, 0.4);
}
.ab-group-row td {
  background: #e3f2fd;
  font-weight: 700;
}
.ab-indent {
  display: inline-flex;
  align-items: center;
}
.ab-toggle {
  margin: 0;
  margin-inline-end: 4px;
  height: 22px;
  width: 22px;
}
.ab-toggle-spacer {
  display: inline-block;
  width: 26px;
}
.ab-negative {
  color: #c62828;
}
.ab-side {
  font-weight: 600;
}
.ab-side-debit {
  color: #1565c0;
}
.ab-side-credit {
  color: #2e7d32;
}
.ab-side-balanced {
  color: rgba(0, 0, 0, 0.55);
}
.ab-note {
  display: inline-block;
  margin-inline-end: 8px;
  font-size: 12px;
}
.ab-note-abnormal,
.ab-note-orphan {
  color: #d84315;
  font-weight: 600;
}
.ab-note-no_movement,
.ab-note-inactive {
  color: rgba(0, 0, 0, 0.5);
}
.ab-match-ok {
  color: #2e7d32;
}
.ab-match-bad {
  color: #c62828;
}
.ab-notes {
  padding: 8px 16px 16px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.ab-notes-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.ab-notes p {
  margin: 0 0 4px;
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
